// Code-based TanStack Router tree. The root route renders the app shell (sidebar
// + content <Outlet/>); each area route wraps its page in <AreaGate> so a
// non-granted employee sees the access-required panel instead of the page.
//
// Deep links: `?q=<text>` on the list pages holds the active filter; the
// person detail uses `$id`, and the conversation reader uses a typed
// `$projectId` path segment. All are typed via
// `validateSearch`, so URLs are shareable and survive back/forward.
// `defaultPreload: "intent"` prefetches on hover/focus; scroll is restored.
import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
  useNavigate,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { ErrorFallback } from "@recruiter/shared";

import { AppShell } from "./components/app-shell";
import { AbTestsPage } from "./pages/ab-tests";
import { AreaGate } from "./components/area-gate";
import {
  type DevelopersMode,
  parseDebugAgent,
  parseDebugAgents,
  parseDevelopersMode,
} from "./lib/debug-agents";
import {
  EXPLORER_AREAS,
  type ExplorerView,
  parseExplorerView,
  parsePersonStatuses,
} from "./lib/explorer-access";
import { parseDeviceParam } from "./lib/device";
import { parseCountryParam } from "./lib/country";
import { parseBudgetParam } from "./lib/budget-range";
import { parseFunnelWindow, type FunnelPresetWindow } from "./lib/funnel-window";
import { parseDayParam, rangeFromSearch, searchForRange } from "./lib/date-range";
import {
  type CohortSection,
  type CohortWindow,
  parseCohortSection,
  parseCohortWindow,
} from "./lib/version-cohorts";
import { armsFromSnapshot, parseScope, parseAbWindow } from "./lib/experiment-scope";
import { experimentationQuery } from "./lib/queries";
import { EmptyState } from "./components/states";
import { AlertsPage } from "./pages/alerts";
import { BigClientsPage } from "./pages/big-clients";
import { DevelopersPage } from "./pages/developers";
import { ExplorerPage } from "./pages/explorer";
import { ExplorerProjectPage } from "./pages/explorer-project";
import { ExplorerTalentPage } from "./pages/explorer-talent";
import { ExplorerUserPage } from "./pages/explorer-user";
import { FunnelPage } from "./pages/funnel";
import { HomePage } from "./pages/home";
import { LaunchPage } from "./pages/launch";
import { PermissionsPage } from "./pages/permissions";
import { SearchDetailPage, SearchesPage } from "./pages/searches";
import { TalentDeclinesPage } from "./pages/talent-declines";
import { ExcludedListPage, OptoutListPage, VipListPage } from "./pages/talent-lists";
import { TalentManagementPage } from "./pages/talent-management";
import { TalentOffersPage } from "./pages/talent-offers";
import { TalentOptoutRequestsPage } from "./pages/talent-optout-requests";
import { TalentPreferencesPage } from "./pages/talent-preferences";
import { ExperimentsPage } from "./pages/experiments";
import { ExperimentDetailPage } from "./pages/experiment-detail";
import {
  type ExperimentTab,
  type RegisterSearch,
  parseExperimentTab,
  parseRegisterSearch,
  registerSearchFrom,
} from "./lib/experimentation";
import { useCanWrite, useWriteAccess } from "./lib/use-access";
import { VersionCohortsPage } from "./pages/version-cohorts";
import { ShortlistTimeframePage } from "./pages/shortlist-timeframe";
import { parseTimeframeWindow } from "./lib/shortlist-timeframe";
import type { TimeframeWindow, AbPresetWindow } from "@recruiter/shared/types";

const rootRoute = createRootRoute({
  component: AppShell,
  notFoundComponent: () => (
    <div className="ad-page">
      <EmptyState title="Page not found" message="That admin page doesn't exist." />
    </div>
  ),
});

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: HomePage,
});

// Launch day: the rollout TV dashboard. Gated on `launch_day` (migration
// 0114) — the payload is aggregate-only and PII-free by contract, but it is
// still a real grant now rather than open to any signed-in employee, so it
// can be handed to someone (e.g. a stakeholder) who should see this ONE
// dashboard and nothing else in the console. Minting the TV's one-time
// remote-view code is a separate, more privileged act, and that button gates
// itself on `system_admin` (backend-enforced either way). The unauthenticated
// TV never reaches this route at all — App.tsx boots it into the kiosk path.
const launchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/launch",
  // The A/B pair lives in the URL, like the Funnel's, so a board read for one
  // arm is a link somebody can be sent. The KIOSK never carries it: the TV path
  // does not reach this route, and the scoped read is employee-gated anyway.
  validateSearch: (search: Record<string, unknown>) => ({
    experiment:
      typeof search.experiment === "string" && search.experiment.length > 0
        ? search.experiment
        : undefined,
    arm: typeof search.arm === "string" && search.arm.length > 0 ? search.arm : undefined,
  }),
  component: function LaunchRouteComponent() {
    const { experiment, arm } = launchRoute.useSearch();
    const navigate = useNavigate({ from: launchRoute.fullPath });
    // Every registry key's own arms, so a link naming a section or injection
    // arm filters instead of parsing as "no filter" (plan §3).
    const registrySnapshot = useQuery(experimentationQuery());
    return (
      <AreaGate area="launch_day">
        <LaunchPage
          scope={parseScope(experiment, arm, armsFromSnapshot(registrySnapshot.data))}
          onScopeChange={(next) =>
            void navigate({
              search: (prev) => ({ ...prev, experiment: next?.experiment, arm: next?.arm }),
            })
          }
        />
      </AreaGate>
    );
  },
});

// The Funnel: project lifecycle conversion. Gated on `funnel` (migration 0115);
// `?window=` holds the cohort so a filtered view is a shareable link, with the
// default ("all") omitted to keep the plain /funnel link clean.
const funnelRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/funnel",
  // `window` picks one of the four PRESET cohorts; `from`/`to` pick an arbitrary
  // one. Both live in the URL for the same reason: a cohort somebody is reading
  // should be a link they can send, not a view they have to be talked through
  // rebuilding. A range WINS over the preset while it is set (the page lights
  // neither preset then), so the two are alternatives rather than a filter on a
  // filter — two cohort definitions at once would be a chart nobody could read.
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    window?: FunnelPresetWindow;
    from?: string;
    to?: string;
    experiment?: string;
    arm?: string;
    devices?: string;
    countries?: string;
    budget_min?: number;
    budget_max?: number;
  } => ({
    window: search.window === undefined ? undefined : parseFunnelWindow(search.window),
    from: parseDayParam(search.from),
    to: parseDayParam(search.to),
    // The A/B group, as two params. Kept as raw strings here and resolved by
    // `parseScope` in the component: an arm without an experiment names
    // nothing, and dropping HALF a scope in the router would leave the URL
    // saying one thing while the page showed another.
    experiment:
      typeof search.experiment === "string" && search.experiment.length > 0
        ? search.experiment
        : undefined,
    arm: typeof search.arm === "string" && search.arm.length > 0 ? search.arm : undefined,
    // The device cohort, as one comma list, so a phones-only funnel is a link
    // somebody can send — the same reason the window and the range live here.
    // Absent = every device, which is the page's default and its cheap lane.
    devices: parseDeviceParam(search.devices),
    // The origin-country cohort, the same comma-list shape for the same reason.
    countries: parseCountryParam(search.countries),
    // The committed-budget bound, USD, as two params so a "over $10k" funnel is
    // a link somebody can send. Absent = unbounded, the default and cheap lane.
    budget_min: parseBudgetParam(search.budget_min),
    budget_max: parseBudgetParam(search.budget_max),
  }),
  component: function FunnelRouteComponent() {
    const { window, from, to, experiment, arm, devices, countries, budget_min, budget_max } =
      funnelRoute.useSearch();
    const navigate = useNavigate({ from: funnelRoute.fullPath });
    // Every registry key's own arms, so a link naming a section or injection
    // arm filters instead of parsing as "no filter" (plan §3).
    const registrySnapshot = useQuery(experimentationQuery());
    return (
      <AreaGate area="funnel">
        <FunnelPage
          window={window ?? "all"}
          onWindowChange={(next) =>
            void navigate({
              search: (prev) => ({ ...prev, window: next === "all" ? undefined : next }),
            })
          }
          range={rangeFromSearch(from, to)}
          onRangeChange={(next) =>
            void navigate({ search: (prev) => ({ ...prev, ...searchForRange(next) }) })
          }
          scope={parseScope(experiment, arm, armsFromSnapshot(registrySnapshot.data))}
          onScopeChange={(next) =>
            void navigate({
              search: (prev) => ({
                ...prev,
                experiment: next?.experiment,
                arm: next?.arm,
              }),
            })
          }
          devices={devices ? devices.split(",") : []}
          onDevicesChange={(next) =>
            void navigate({
              // Empty is "all devices", which is the default — so it leaves the
              // URL rather than sitting in it as `?devices=`.
              search: (prev) => ({
                ...prev,
                devices: next.length > 0 ? next.join(",") : undefined,
              }),
            })
          }
          countries={countries ? countries.split(",") : []}
          onCountriesChange={(next) =>
            void navigate({
              search: (prev) => ({
                ...prev,
                countries: next.length > 0 ? next.join(",") : undefined,
              }),
            })
          }
          budget={{ min: budget_min, max: budget_max }}
          onBudgetChange={(next) =>
            void navigate({
              // An open side leaves the URL rather than sitting in it empty.
              search: (prev) => ({ ...prev, budget_min: next.min, budget_max: next.max }),
            })
          }
        />
      </AreaGate>
    );
  },
});

/** The Explorer's `?stage=` filter: a comma list of stage-ladder keys. Written
 *  by the Funnel's node clicks, and now also by the Stage control above the
 *  projects table. Blank entries drop out; empty = absent.
 *
 *  Deliberately NOT checked against `PROJECT_STAGES`. That ladder is an OPEN
 *  wire union — the backend can add a rung before this bundle ships
 *  (lib/project-stage.ts) — and now that the control is editable, validating
 *  here would make picking that new rung a silent no-op: the URL would swallow
 *  it and the table would never narrow. An unrecognized key instead matches
 *  nothing, stays listed in the popover so it can be unticked, and goes with
 *  "Clear filters" like anything else. */
function parseStageParam(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const keys = value.split(",").filter((s) => s !== "");
  return keys.length > 0 ? keys.join(",") : undefined;
}

/** The Explorer's `?status=` filter, the same comma-list shape. `?status=blocked`
 *  is the blocked-account roster somebody may have bookmarked, so it is normalized
 *  through the canonical tuple rather than passed through raw. */
function parseStatusParam(value: unknown): string | undefined {
  const known = parsePersonStatuses(value);
  return known.length > 0 ? known.join(",") : undefined;
}

// --- Explorer -----------------------------------------------------------------
// The console's single browse surface, and the home of what used to be three
// pages: its own, User Administration's users table, and Customer Support's
// people/person/conversation views. One URL per entity, shareable by anyone who
// can open it; `?q=` holds the search so a filtered view survives a reload.
//
// Gated on `explorer` OR `customer_support` — the same screens serve both, with
// support-mode viewers reading the narrow talent-free payload (lib/explorer-access.ts).
const explorerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/explorer",
  // `view` defaults to "people" and is omitted from the URL in that case, so the
  // plain /explorer link stays clean; ?view=projects is the "all projects" view.
  // `stage` and `status` are the two projects/people filters that live in the
  // URL rather than in component state, both comma lists: the Funnel's node
  // clicks write `stage`, the Stage and Status controls above the tables edit
  // both, and `?status=blocked` IS the blocked-account roster. Keeping them here
  // is what makes "every project at Concierge" a link somebody can be sent
  // rather than a view they have to be talked through rebuilding.
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    q?: string;
    view?: ExplorerView;
    stage?: string;
    status?: string;
    from?: string;
    to?: string;
    experiment?: string;
    arm?: string;
  } => ({
    q: typeof search.q === "string" && search.q.length > 0 ? search.q : undefined,
    view: search.view === undefined ? undefined : parseExplorerView(search.view),
    stage: parseStageParam(search.stage),
    status: parseStatusParam(search.status),
    // The date range, here for the same reason `stage` and `status` are: it is
    // half of "every project touched during the incident", which is a link
    // somebody should be able to send rather than a view they rebuild.
    from: parseDayParam(search.from),
    to: parseDayParam(search.to),
    // The A/B group, kept as raw strings and resolved by `parseScope` below —
    // an arm without an experiment names nothing, and dropping half a scope
    // here would leave the URL saying one thing and the page showing another.
    experiment:
      typeof search.experiment === "string" && search.experiment.length > 0
        ? search.experiment
        : undefined,
    arm: typeof search.arm === "string" && search.arm.length > 0 ? search.arm : undefined,
  }),
  component: function ExplorerRouteComponent() {
    const { q, view, stage, status, from, to, experiment, arm } = explorerRoute.useSearch();
    // Accept ANY registry key's own arms, not just the two product ones, so a
    // link naming a section's `absent` arm filters instead of silently showing
    // everybody (plan §3).
    const registrySnapshot = useQuery(experimentationQuery());
    const navigate = useNavigate({ from: explorerRoute.fullPath });
    /** Write a comma-list filter back, dropping the key when it empties so a
     *  cleared filter leaves a clean URL rather than `?stage=`. */
    const setListParam = (key: "stage" | "status") => (next: readonly string[]) =>
      void navigate({
        search: (prev) => ({ ...prev, [key]: next.length > 0 ? next.join(",") : undefined }),
      });
    return (
      <AreaGate area={EXPLORER_AREAS}>
        <ExplorerPage
          q={q ?? ""}
          onQChange={(next) =>
            void navigate({
              search: (prev) => ({ ...prev, q: next.length > 0 ? next : undefined }),
            })
          }
          view={view ?? "people"}
          onViewChange={(next) =>
            void navigate({
              search: (prev) => ({ ...prev, view: next === "people" ? undefined : next }),
            })
          }
          stageFilter={stage ? stage.split(",") : undefined}
          onStageChange={setListParam("stage")}
          statusFilter={status ? status.split(",") : undefined}
          onStatusChange={setListParam("status")}
          scope={parseScope(experiment, arm, armsFromSnapshot(registrySnapshot.data))}
          onScopeChange={(next) =>
            void navigate({
              search: (prev) => ({
                ...prev,
                experiment: next?.experiment,
                arm: next?.arm,
              }),
            })
          }
          range={rangeFromSearch(from, to)}
          onRangeChange={(next) =>
            void navigate({ search: (prev) => ({ ...prev, ...searchForRange(next) }) })
          }
        />
      </AreaGate>
    );
  },
});

const explorerUserRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/explorer/users/$id",
  component: function ExplorerUserRouteComponent() {
    const { id } = explorerUserRoute.useParams();
    return (
      <AreaGate area={EXPLORER_AREAS}>
        <ExplorerUserPage id={id} />
      </AreaGate>
    );
  },
});

// The destination of every project deep link in a Slack alert
// (recruiter/alerts.py::_project_link). Changing this path means changing that
// builder in the same PR: the console renders "Page not found" for an unknown
// path rather than redirecting, so a drifted link dead-ends silently.
const explorerProjectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/explorer/projects/$projectId",
  component: function ExplorerProjectRouteComponent() {
    const { projectId } = explorerProjectRoute.useParams();
    return (
      <AreaGate area={EXPLORER_AREAS}>
        {/* Keyed on the project id: the router reuses this component instance when
            only the param changes, so any lazily-initialized state in the detail
            tree would survive a hop from /explorer/projects/A to /B and show A's
            values under B's URL - the disease #1061 fixed field-by-field on the
            talent page. The key remounts the whole page per project instead. */}
        <ExplorerProjectPage key={projectId} projectId={projectId} />
      </AreaGate>
    );
  },
});

// One talent, across every project we approached them for. `$sellerId` IS their
// Fiverr username (`seller_threads.seller_id`), so the URL is human-readable and
// pasteable. Gated with the rest of the Explorer: the page's operational half
// reads the talent-bearing route, and its statistics half degrades to a note for
// a viewer without `talent_management` rather than gating the whole page.
const explorerTalentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/explorer/talents/$sellerId",
  // `?project=` preselects ONE engagement. It exists so a link arriving from a search
  // or a project opens that thread instead of dumping the reader into a list of every
  // project we ever approached this talent for. Typed, so the scoped view is shareable
  // and survives a refresh — the selection used to be local state and was neither.
  validateSearch: (search: Record<string, unknown>): { project?: string } => ({
    project:
      typeof search.project === "string" && search.project.length > 0 ? search.project : undefined,
  }),
  component: function ExplorerTalentRouteComponent() {
    const { sellerId } = explorerTalentRoute.useParams();
    const { project } = explorerTalentRoute.useSearch();
    return (
      <AreaGate area={EXPLORER_AREAS}>
        <ExplorerTalentPage sellerId={sellerId} projectId={project} />
      </AreaGate>
    );
  },
});

const alertsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/alerts",
  component: function AlertsRouteComponent() {
    return (
      <AreaGate area="alerts">
        <AlertsPage />
      </AreaGate>
    );
  },
});

// Alerts' second lane: the big-fish desk. A ROUTE, not local state, so an operator
// can send someone straight to it — and so switching lanes does not refetch the
// worklist.
//
// Gated `alerts` OR `explorer`, because the page is two halves with different
// needs: the alert LIST plus Stop is plain employee scope, while the conversation/
// brief/shortlist detail reads the talent-bearing project route (gated `explorer`
// OR `talent_management`). Holding either opens the page; the page itself tells a
// viewer without Explorer why the detail is closed to them, instead of handing
// them a 403 (see pages/big-clients.tsx).
const bigClientsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/alerts/big-clients",
  component: function BigClientsRouteComponent() {
    return (
      <AreaGate area={["alerts", "explorer"]}>
        <BigClientsPage />
      </AreaGate>
    );
  },
});

// --- Customer Support: folded into the Explorer -------------------------------
// Its list, person page and conversation reader were duplicates of the Explorer's
// three screens, differing only in the payload they read. The grant survives and
// now opens the Explorer in support mode; the pages are gone and their paths
// redirect, one-for-one, so every existing bookmark and Slack link still lands on
// the same record.
//
// These redirects are deliberately NOT area-gated: each resolves to a route that
// gates itself, and gating the hop would show "access required" to someone on
// their way to a page they can in fact see.

const legacyCustomerSupportRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/customer-support",
  validateSearch: (search: Record<string, unknown>): { q?: string } => ({
    q: typeof search.q === "string" && search.q.length > 0 ? search.q : undefined,
  }),
  beforeLoad: ({ search }) => {
    throw redirect({ to: "/explorer", search: { q: search.q } });
  },
});

const legacyConversationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/customer-support/conversations/$projectId",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/explorer/projects/$projectId",
      params: { projectId: params.projectId },
    });
  },
});

const legacyPersonRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/customer-support/people/$id",
  beforeLoad: ({ params }) => {
    throw redirect({ to: "/explorer/users/$id", params: { id: params.id } });
  },
});

const talentManagementRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/talent-management",
  component: function TalentManagementRouteComponent() {
    return (
      <AreaGate area="talent_management">
        <TalentManagementPage />
      </AreaGate>
    );
  },
});

// Talent Management's two tabs are two ROUTES, not local state: a search someone
// is looking at should be linkable and survive a refresh, and the dashboard's
// snapshot query should not refetch just because a tab was toggled.
const talentSearchesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/talent-management/searches",
  component: function TalentSearchesRouteComponent() {
    return (
      <AreaGate area="talent_management">
        <SearchesPage />
      </AreaGate>
    );
  },
});

const talentOffersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/talent-management/offers",
  component: function TalentOffersRouteComponent() {
    return (
      <AreaGate area="talent_management">
        <TalentOffersPage />
      </AreaGate>
    );
  },
});

const talentVipRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/talent-management/vip",
  component: function TalentVipRouteComponent() {
    return (
      <AreaGate area="talent_management">
        <VipListPage />
      </AreaGate>
    );
  },
});

const talentExcludedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/talent-management/excluded",
  component: function TalentExcludedRouteComponent() {
    return (
      <AreaGate area="talent_management">
        <ExcludedListPage />
      </AreaGate>
    );
  },
});

const talentOptoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/talent-management/optout",
  component: function TalentOptoutRouteComponent() {
    return (
      <AreaGate area="talent_management">
        <OptoutListPage />
      </AreaGate>
    );
  },
});

// The queue that feeds the list above: talents who ASKED to stop being contacted
// (migration 0120). A separate route, not a tab toggle, because it is the
// destination of the "talent opt-out" Slack alert
// (`recruiter/alerts.py::TalentOptOutAlert`) — changing this path means changing
// that builder in the same PR, since the router renders "Page not found" for an
// unknown path rather than redirecting, so a drifted link dead-ends silently.
const talentOptoutRequestsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/talent-management/optout-requests",
  component: function TalentOptoutRequestsRouteComponent() {
    return (
      <AreaGate area="talent_management">
        <TalentOptoutRequestsPage />
      </AreaGate>
    );
  },
});

// The talent-preference review queue. A preference approved here starts REMOVING that
// talent from client searches, so it is `talent_management` like the rest of the section
// and the page itself drops the decision column for a read-only holder.
const talentPreferencesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/talent-management/preferences",
  component: function TalentPreferencesRouteComponent() {
    return (
      <AreaGate area="talent_management">
        <TalentPreferencesPage />
      </AreaGate>
    );
  },
});

// Cross-project declines: why talents are saying no, in their own words.
const talentDeclinesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/talent-management/declines",
  component: function TalentDeclinesRouteComponent() {
    return (
      <AreaGate area="talent_management">
        <TalentDeclinesPage />
      </AreaGate>
    );
  },
});

const talentSearchDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/talent-management/searches/$searchId",
  component: function TalentSearchDetailRouteComponent() {
    const { searchId } = talentSearchDetailRoute.useParams();
    return (
      <AreaGate area="talent_management">
        <SearchDetailPage searchId={searchId} />
      </AreaGate>
    );
  },
});

// User Administration no longer has a page. Its users table was a second copy of
// the Explorer's people list, so it was deleted and its Block/Delete buttons
// moved onto the Explorer, where they render only for holders of the grant
// (components/user-admin-actions.tsx).
//
// The path survives as a redirect, carrying any `?q=` straight into the
// Explorer's search so an old bookmark lands on the same filtered list rather
// than the not-found panel. Deliberately NOT area-gated: it resolves to a route
// that gates itself, and gating a redirect would show "access required" for
// `user_administration` to someone on their way to a page they can see.
const legacyUserAdministrationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/user-administration",
  validateSearch: (search: Record<string, unknown>): { q?: string } => ({
    q: typeof search.q === "string" && search.q.length > 0 ? search.q : undefined,
  }),
  beforeLoad: ({ search }) => {
    throw redirect({ to: "/explorer", search: { q: search.q } });
  },
});

// --- Developers ----------------------------------------------------------------
// The agent debug dashboard: every LLM call every agent made, per turn. Its whole
// selection lives in the URL — project, turn, agent tab, and which of the two
// views you are in — because the point of a debugging surface is being able to
// send someone the exact trace you are looking at. A bare /developers opens the
// projects list, so the clean link stays clean — and `?agents=` on its own is a
// whole-store search (every project's turns in which that agent fired), so THAT
// is sendable too, without having to name a project first.
// Version Cohorts: the same metrics either side of one recorded deploy. Gated on
// `version_cohorts` (migration 0131). BOTH controls live in the URL so a
// comparison is a link somebody can be sent rather than a view they have to be
// talked through rebuilding: `?release=` is the boundary and `?window=` is how
// much of each side is compared. The defaults are omitted from the URL (release
// absent means "the newest recorded deploy", which is what a bare link should
// show), so /version-cohorts stays clean.
const versionCohortsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/version-cohorts",
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    window?: CohortWindow;
    release?: string;
    section?: CohortSection;
    devices?: string;
    countries?: string;
  } => ({
    window: search.window === undefined ? undefined : parseCohortWindow(search.window),
    // An opaque release id (a commit sha, or a Cloud Run revision name) —
    // validated as a non-empty string and resolved by the fetch, which 404s an
    // unknown one honestly rather than having the router guess at a format or
    // silently fall back to the newest deploy.
    release:
      typeof search.release === "string" && search.release.length > 0 ? search.release : undefined,
    // Which SUBJECT's cards are showing (the page renders one at a time).
    // Absent means "the first subject with data", so a bare link stays clean.
    section: search.section === undefined ? undefined : parseCohortSection(search.section),
    // The cohort, as two comma lists — the Funnel's shape, so a phones-in-India
    // comparison is a link somebody can send. Absent = everyone, the scheduled
    // whole-population read; either one set moves the page to the on-demand slice.
    devices: parseDeviceParam(search.devices),
    countries: parseCountryParam(search.countries),
  }),
  component: function VersionCohortsRouteComponent() {
    const { window, release, section, devices, countries } = versionCohortsRoute.useSearch();
    const navigate = useNavigate({ from: versionCohortsRoute.fullPath });
    return (
      <AreaGate area="version_cohorts">
        <VersionCohortsPage
          window={window ?? "14d"}
          onWindowChange={(next) =>
            void navigate({
              search: (prev) => ({ ...prev, window: next === "14d" ? undefined : next }),
            })
          }
          release={release}
          onReleaseChange={(next) =>
            void navigate({ search: (prev) => ({ ...prev, release: next }) })
          }
          section={section}
          onSectionChange={(next) =>
            void navigate({ search: (prev) => ({ ...prev, section: next }) })
          }
          devices={devices ? devices.split(",") : []}
          onDevicesChange={(next) =>
            void navigate({
              search: (prev) => ({
                ...prev,
                devices: next.length > 0 ? next.join(",") : undefined,
              }),
            })
          }
          countries={countries ? countries.split(",") : []}
          onCountriesChange={(next) =>
            void navigate({
              search: (prev) => ({
                ...prev,
                countries: next.length > 0 ? next.join(",") : undefined,
              }),
            })
          }
        />
      </AreaGate>
    );
  },
});

// The shortlist-timeframe readout (Monday 3204302464): what happened, by the
// timeframe the buyer chose at the start-choice picker. The third tab of the
// cohort strip, on the same `version_cohorts` grant (same aggregate-only
// contract, same reader). Its one control, the roll-up window, lives in the
// URL so a reading is a link; the default (`30d`) is omitted to keep it clean.
const shortlistTimeframeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/shortlist-timeframe",
  validateSearch: (search: Record<string, unknown>): { window?: TimeframeWindow } => ({
    window: search.window === undefined ? undefined : parseTimeframeWindow(search.window),
  }),
  component: function ShortlistTimeframeRouteComponent() {
    const { window } = shortlistTimeframeRoute.useSearch();
    const navigate = useNavigate({ from: shortlistTimeframeRoute.fullPath });
    return (
      <AreaGate area="version_cohorts">
        <ShortlistTimeframePage
          window={window ?? "30d"}
          onWindowChange={(next) =>
            void navigate({
              search: (prev) => ({ ...prev, window: next === "30d" ? undefined : next }),
            })
          }
        />
      </AreaGate>
    );
  },
});

// The PLAYGROUND (its OWN area since migration 0168, `ab_tests`): the A/B readout,
// with a tab per registered experiment. RESTORED 2026-09-15 — #1231 turned this into a
// redirect onto the experiments register when the readout became each experiment's
// Outcomes tab, which left the sidebar promising "Playground" and delivering a list of
// ninety-six experiments. The readout still lives on each experiment's page too; this is
// the surface where the experiment is CHOSEN rather than fixed by the route.
//
// `?experiment=` keeps its old meaning and is no longer a redirect, so a bookmark that
// named an experiment selects it here instead of bouncing to that experiment's page.
const abTestsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ab-tests",
  validateSearch: (
    search: Record<string, unknown>,
  ): { experiment?: string; window?: AbPresetWindow; from?: string; to?: string } => ({
    // An opaque experiment name, validated as a non-empty string: the registry
    // lives in the backend, and the route resolves an unknown one to the first
    // registered experiment rather than having the router guess at a vocabulary.
    experiment:
      typeof search.experiment === "string" && search.experiment.length > 0
        ? search.experiment
        : undefined,
    window: parseAbWindow(search.window),
    // The cohort range, same `?from=&to=` pair every other filtered board uses,
    // so a range is a link somebody can be sent. Absent = use the preset.
    from: parseDayParam(search.from),
    to: parseDayParam(search.to),
  }),
  component: function AbTestsRouteComponent() {
    const { experiment, window: win, from, to } = abTestsRoute.useSearch();
    const navigate = useNavigate({ from: abTestsRoute.fullPath });
    return (
      <AreaGate area="ab_tests">
        <AbTestsPage
          experiment={experiment}
          onExperimentChange={(next) =>
            void navigate({ search: (prev) => ({ ...prev, experiment: next }) })
          }
          window={win ?? "all"}
          onWindowChange={(next) =>
            void navigate({
              search: (prev) => ({ ...prev, window: next === "all" ? undefined : next }),
            })
          }
          range={rangeFromSearch(from, to)}
          onRangeChange={(next) =>
            void navigate({ search: (prev) => ({ ...prev, ...searchForRange(next) }) })
          }
        />
      </AreaGate>
    );
  },
});

const developersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/developers",
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    q?: string;
    project?: string;
    turn?: string;
    agent?: string;
    agents?: string;
    mode?: DevelopersMode;
  } => ({
    q: typeof search.q === "string" && search.q.length > 0 ? search.q : undefined,
    // `project` and `turn` are opaque ids (a uuid, or the literal "none" bucket) —
    // validated as non-empty strings and resolved by the fetch, which 404s a bad
    // one honestly rather than having the router guess at a format.
    project:
      typeof search.project === "string" && search.project.length > 0 ? search.project : undefined,
    turn: typeof search.turn === "string" && search.turn.length > 0 ? search.turn : undefined,
    agent: search.agent === undefined ? undefined : parseDebugAgent(search.agent),
    // The turns-list filter, comma-separated. Round-tripped through the parser so
    // the URL only ever carries real agent keys, and dropped when empty (no
    // filter is the default, and a bare ?agents= is noise in a shared link).
    agents: parseDebugAgents(search.agents).join(",") || undefined,
    // "scoped" is the default, so it is omitted from the URL entirely.
    mode: search.mode === undefined ? undefined : parseDevelopersMode(search.mode),
  }),
  component: function DevelopersRouteComponent() {
    const { q, project, turn, agent, agents, mode } = developersRoute.useSearch();
    const navigate = useNavigate({ from: developersRoute.fullPath });
    const set = (next: Record<string, unknown>) =>
      void navigate({ search: (prev) => ({ ...prev, ...next }) });
    return (
      <AreaGate area="developers">
        <DevelopersPage
          q={q ?? ""}
          onQChange={(next) => set({ q: next.length > 0 ? next : undefined })}
          project={project}
          onProjectChange={(next) => set({ project: next })}
          turn={turn}
          onTurnChange={(next) => set({ turn: next })}
          agent={parseDebugAgent(agent)}
          onAgentChange={(next) => set({ agent: next })}
          agents={parseDebugAgents(agents)}
          onAgentsChange={(next) => set({ agents: next.length > 0 ? next.join(",") : undefined })}
          mode={parseDevelopersMode(mode)}
          onModeChange={(next) => set({ mode: next === "scoped" ? undefined : next })}
        />
      </AreaGate>
    );
  },
});

// Permission management lives under System Admin, NOT under User Administration:
// granting an area is authority over the console's operators, while
// user_administration is authority over the product's users. Fusing them let
// anyone holding the customer-support-facing gate grant themselves any area.
// Prompt sections live under System Admin: changing what a client's prompt says
// is authority over the product's behaviour, which is the same class of control
// as the A/B dials and the Red Button rather than an operator task.
// **A redirect.** The prompt-section console was the only page that showed what
// a thing under test actually SAYS, and it did it for one kind, with no search.
// The register now opens that same panel for all six, so keeping a second
// surface would mean two places to move a section's dial from and two places to
// keep correct. The path stays because Slack links and bookmarks point at it,
// and it is deliberately NOT area-gated: it resolves to a route that gates
// itself, so a redirect never renders the access panel for somebody who would
// have been let through.
const promptSectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/prompt-sections",
  beforeLoad: () => {
    throw redirect({ to: "/experiments", search: { kind: "section" } });
  },
  component: () => null,
});

// The experiment register sits under System Admin for the same reason the prompt
// sections do: it is the read side of the same class of control.
const experimentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/experiments",
  // The register's whole view lives here: `?tab=outcomes` is what the old
  // /ab-tests path redirects to, and the rest is the filter/sort/saved-view
  // state, so a narrowed board is a link somebody can paste into a channel.
  // Unrecognised values fall back to the default board rather than throwing: a
  // stale bookmark should still open the page.
  validateSearch: (search: Record<string, unknown>): RegisterSearch & { tab?: "outcomes" } => {
    const { filters, sort, view } = parseRegisterSearch(search);
    return {
      ...registerSearchFrom(filters, sort, view),
      tab: search.tab === "outcomes" ? "outcomes" : undefined,
    };
  },
  component: function ExperimentsRouteComponent() {
    const search = experimentsRoute.useSearch();
    const navigate = useNavigate({ from: experimentsRoute.fullPath });
    const { filters, sort, view } = parseRegisterSearch(search as Record<string, unknown>);
    const canAct = useCanWrite("system_admin");
    const { known } = useWriteAccess("system_admin");
    return (
      <AreaGate area={["system_admin", "ab_tests"]}>
        <ExperimentsPage
          outcomesHint={search.tab === "outcomes"}
          filters={filters}
          sort={sort}
          view={view}
          canAct={canAct}
          accessKnown={known}
          onSearchChange={(next) =>
            void navigate({ search: { ...next, tab: search.tab } })
          }
        />
      </AreaGate>
    );
  },
});

// One experiment's page: the allocation control and the evidence behind it in
// tabs. Read by `system_admin` OR `ab_tests` (the grant that owned the A/B
// readout this page now hosts), matching the backend union; the controls need
// `system_admin` at full (`canAct`), and the Outcomes tab checks `ab_tests`
// itself, in a sentence rather than a panel.
const experimentDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/experiments/$key",
  validateSearch: (search: Record<string, unknown>): { tab?: ExperimentTab } => ({
    tab: parseExperimentTab(search.tab),
  }),
  component: function ExperimentDetailRouteComponent() {
    const { key } = experimentDetailRoute.useParams();
    const { tab } = experimentDetailRoute.useSearch();
    const navigate = useNavigate({ from: experimentDetailRoute.fullPath });
    const canAct = useCanWrite("system_admin");
    const { known } = useWriteAccess("system_admin");
    return (
      <AreaGate area={["system_admin", "ab_tests"]}>
        <ExperimentDetailPage
          experimentKey={key}
          tab={tab}
          onTabChange={(next) => void navigate({ search: { tab: next } })}
          canAct={canAct}
          accessKnown={known}
        />
      </AreaGate>
    );
  },
});

const systemAdminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/system-admin",
  component: function SystemAdminRouteComponent() {
    return (
      <AreaGate area="system_admin">
        <PermissionsPage />
      </AreaGate>
    );
  },
});

// The old path, kept as a redirect so existing bookmarks and any link still
// pointing at the User Administration sub-page land in the right place.
const legacyPermissionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/user-administration/permissions",
  beforeLoad: () => {
    throw redirect({ to: "/system-admin" });
  },
});

const routeTree = rootRoute.addChildren([
  homeRoute,
  launchRoute,
  funnelRoute,
  explorerRoute,
  explorerUserRoute,
  explorerProjectRoute,
  explorerTalentRoute,
  alertsRoute,
  bigClientsRoute,
  legacyCustomerSupportRoute,
  legacyPersonRoute,
  legacyConversationRoute,
  talentManagementRoute,
  talentSearchesRoute,
  talentOffersRoute,
  talentDeclinesRoute,
  talentVipRoute,
  talentExcludedRoute,
  talentOptoutRoute,
  talentOptoutRequestsRoute,
  talentPreferencesRoute,
  talentSearchDetailRoute,
  legacyUserAdministrationRoute,
  developersRoute,
  versionCohortsRoute,
  shortlistTimeframeRoute,
  abTestsRoute,
  promptSectionsRoute,
  experimentsRoute,
  experimentDetailRoute,
  systemAdminRoute,
  legacyPermissionsRoute,
]);

// A page that throws must not take the CONSOLE with it.
//
// TanStack only wraps a route in its CatchBoundary when an error component
// exists (`ResolvedCatchBoundary` in its Match.js is a plain Fragment
// otherwise), so with none declared every render failure fell straight through
// to the root boundary in `main.tsx` and replaced the whole app — sidebar,
// nav and all — with "Something went wrong". That is what an operator reports
// as "the screen went blank": one bad row in one table, and the console is
// gone with no way back but a reload.
//
// With this, the failure is contained to the outlet: the shell stays, every
// other area is still one click away, and "Try again" re-renders the route
// without a reload. The root boundary stays where it is as the last resort for
// a crash in the shell itself. React still reports the caught error through
// `createRootErrorHandlers` (the boundary is an ordinary React one), so the
// containment costs no diagnosability.
function RouteErrorPanel({ reset }: ErrorComponentProps) {
  return (
    <div className="ad-page">
      <ErrorFallback
        variant="section"
        title="This page hit an error"
        message="The rest of the console still works. Try again, or reload if it keeps happening."
        primaryAction={{ label: "Try again", onClick: reset }}
        secondaryAction={{ label: "Reload page", onClick: () => window.location.reload() }}
      />
    </div>
  );
}

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  scrollRestoration: true,
  defaultErrorComponent: RouteErrorPanel,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
